import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { BoltIcon, CloseCircleIcon, LockIcon } from '../../../../../assets/icons/index';
import {
  VESTING_GRAPH_TOOLTIP_CURSOR_COLOR,
  vestingGraphPercentToOverlayTopPercent,
} from '../model/vesting-graph.constants';
import { VestingGraphEventMarkerStack } from '../model/vesting-graph.types';
import { VestingGraphIntroReveal } from './vesting-graph-intro-reveal';
import { AnimatedFixedTooltip } from './vesting-graph-tooltip';
import { useVestingGraphContext } from './vesting-graph-view-context';

const PERIOD_BRACKET_TRANSITION_MS = 220;

type BracketTone = 'brand' | 'cliff';

const BRACKET_BADGE_CLASS: Record<BracketTone, string> = {
  brand: 'bg-brand-500',
  cliff: 'bg-sunbeam-400',
};

const BRACKET_RULE_CLASS: Record<BracketTone, string> = {
  brand: 'border-brand-500',
  cliff: 'border-sunbeam-400',
};

const BRACKET_ARROW_CLASS: Record<BracketTone, { left: string; right: string }> = {
  brand: { left: 'border-r-brand-500', right: 'border-l-brand-500' },
  cliff: { left: 'border-r-sunbeam-400', right: 'border-l-sunbeam-400' },
};

type VestingGraphMilestoneBadgeProps = {
  className?: string;
  icon?: ReactNode;
  label?: ReactNode;
};

const VestingGraphMilestoneBadge: FC<VestingGraphMilestoneBadgeProps> = ({
  className,
  icon,
  label,
}) => (
  <span
    className={twMerge(
      'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-semibold text-white shadow-sm',
      className,
    )}
  >
    {icon}
    {label}
  </span>
);

type VestingGraphBracketRuleProps = {
  badgeClassName?: string;
  className?: string;
  icon?: ReactNode;
  label: ReactNode;
  tone: BracketTone;
};

/** Triangular cap drawn from CSS borders, pointing outwards at the end of a bracket. */
const VestingGraphBracketArrow: FC<{ side: 'left' | 'right'; tone: BracketTone }> = ({
  side,
  tone,
}) => (
  <span
    className={twMerge(
      'absolute top-1/2 z-0 h-0 w-0 -translate-y-1/2 border-y-4 border-solid border-y-transparent',
      side === 'left' ? 'left-0 border-r-4' : 'right-0 border-l-4',
      side === 'left' ? BRACKET_ARROW_CLASS[tone].left : BRACKET_ARROW_CLASS[tone].right,
    )}
  />
);

/** A labelled rule with arrow ends, spanning whatever width its container was given. */
const VestingGraphBracketRule: FC<VestingGraphBracketRuleProps> = ({
  badgeClassName,
  className,
  icon,
  label,
  tone,
}) => (
  <div className={twMerge('relative', className)}>
    <div className={twMerge('absolute inset-x-0 top-1/2 z-0 border-t', BRACKET_RULE_CLASS[tone])} />
    <VestingGraphBracketArrow side="left" tone={tone} />
    <VestingGraphBracketArrow side="right" tone={tone} />
    {/* Badge above the rule so the stroke never cuts through the label. */}
    <div className="relative z-[1] flex justify-center">
      <VestingGraphMilestoneBadge
        className={twMerge(BRACKET_BADGE_CLASS[tone], badgeClassName)}
        icon={icon}
        label={label}
      />
    </div>
  </div>
);

/** Column washes sit under the series; badges sit above so the SVG never covers the plaques. */
export type VestingGraphOverlayLayer = 'badge' | 'column';

type VestingGraphCliffBracketProps = {
  label: ReactNode;
  leftPercent: number;
  tooltip?: ReactNode;
  widthPercent: number;
  yPercent: number;
};

/**
 * Sits at the height the cliff releases, spanning the months that release nothing, with the
 * withheld part of the plot washed underneath so the span reads without counting months.
 */
export const VestingGraphCliffBracket: FC<VestingGraphCliffBracketProps> = ({
  label,
  leftPercent,
  tooltip,
  widthPercent,
  yPercent,
}) => {
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(
    null,
  );
  const overlayTopPercent = vestingGraphPercentToOverlayTopPercent(yPercent);
  const {
    view: { introDelayForReveal, introPhase, isHoverLive },
  } = useVestingGraphContext();

  return (
    <>
      <div
        className="pointer-events-none absolute bottom-0"
        style={{
          left: `${leftPercent}%`,
          top: `${overlayTopPercent}%`,
          width: `${widthPercent}%`,
        }}
      >
        <VestingGraphIntroReveal
          className="relative h-full"
          delayMs={introDelayForReveal(leftPercent)}
          phase={introPhase}
        >
          {/* Hangs off the rule and fades out, and is cut short by a cliff that releases early. */}
          <div className="absolute inset-x-0 top-0 h-8 max-h-full bg-gradient-to-b from-sunbeam-50 to-transparent" />
          <VestingGraphBracketRule
            badgeClassName="rounded"
            className="-translate-y-1/2"
            icon={<LockIcon fillColor="#FFFFFF" height={12} iconColor="#FFFFFF" width={12} />}
            label={label}
            tone="cliff"
          />
        </VestingGraphIntroReveal>
      </div>
      {tooltip && (
        <div
          className="absolute bottom-0 top-0 cursor-default"
          onMouseEnter={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            setTooltipPosition({
              left: rect.left + rect.width / 2,
              top: rect.top + (rect.height * overlayTopPercent) / 100 + 20,
            });
          }}
          onMouseLeave={() => setTooltipPosition(null)}
          style={{
            left: `${leftPercent}%`,
            pointerEvents: isHoverLive ? 'auto' : 'none',
            width: `${widthPercent}%`,
          }}
        />
      )}
      <AnimatedFixedTooltip
        left={tooltipPosition?.left ?? 0}
        open={isHoverLive && tooltipPosition !== null && Boolean(tooltip)}
        top={tooltipPosition?.top ?? 0}
        transform="translate(-50%, 0)"
      >
        {tooltip}
      </AnimatedFixedTooltip>
    </>
  );
};

type VestingGraphPeriodBracketProps = {
  label: ReactNode;
  layer: VestingGraphOverlayLayer;
  leftPercent: number;
  open: boolean;
  widthPercent: number;
};

/**
 * Frames the hovered dynamic vesting period. Stays mounted through leave so the wash
 * and badge can fade out; left/width ease when the pointer moves to another period.
 */
export const VestingGraphPeriodBracket: FC<VestingGraphPeriodBracketProps> = ({
  label,
  layer,
  leftPercent,
  open,
  widthPercent,
}) => {
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  const cachedRef = useRef({ label, leftPercent, widthPercent });

  if (open) {
    cachedRef.current = { label, leftPercent, widthPercent };
  }

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }

    setEntered(false);
    const timeoutId = window.setTimeout(() => setMounted(false), PERIOD_BRACKET_TRANSITION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  useEffect(() => {
    if (!mounted || !open) return;

    let innerFrame = 0;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => setEntered(true));
    });

    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, [mounted, open]);

  if (!mounted) return null;

  const { label: shellLabel, leftPercent: shellLeft, widthPercent: shellWidth } = cachedRef.current;

  return (
    <div
      className={twMerge(
        'pointer-events-none absolute bottom-0 top-0 will-change-[left,width,opacity,transform]',
        'transition-[left,width,opacity,transform] duration-200 ease-out',
        entered ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0',
      )}
      style={{ left: `${shellLeft}%`, width: `${shellWidth}%` }}
    >
      {layer === 'column' && (
        <div className="absolute inset-0 border-x border-dashed border-brand-500" />
      )}
      {layer === 'badge' && (
        <VestingGraphBracketRule
          badgeClassName="rounded"
          className="-translate-y-1/2"
          label={shellLabel}
          tone="brand"
        />
      )}
    </div>
  );
};

type VestingGraphSkinnyColumnMarkerProps = {
  badgeClassName: string;
  /** Clears the same baseline gutter the value bars get from the negative Y-domain floor. */
  bottomInsetPx: number;
  columnClassName: string;
  icon: ReactNode;
  label: ReactNode;
  layer: VestingGraphOverlayLayer;
  leftPercent: number;
  stack?: VestingGraphEventMarkerStack;
  /** Fixed 15px slot — a skinny marker, not a payment-width bar. */
  widthPx: number;
};

const BADGE_STACK_TRANSFORM: Record<VestingGraphEventMarkerStack, string> = {
  above: 'translate(-50%, calc(-100% - 2px))',
  below: 'translate(-50%, 2px)',
  center: 'translate(-50%, -50%)',
};

/**
 * Skinny dashed column: a top-fading wash and a cliff-style stretch on top. The badge keeps
 * its caption. When two event columns sit next to each other, `stack` puts one pill above
 * the column top and the other just below it so the words stay readable.
 */
const VestingGraphSkinnyColumnMarker: FC<VestingGraphSkinnyColumnMarkerProps> = ({
  badgeClassName,
  bottomInsetPx,
  columnClassName,
  icon,
  label,
  layer,
  leftPercent,
  stack = 'center',
  widthPx,
}) => {
  const {
    view: { introDelayForReveal, introPhase },
  } = useVestingGraphContext();

  return (
    <div
      className="pointer-events-none absolute top-0 -translate-x-1/2"
      style={{ bottom: bottomInsetPx, left: `${leftPercent}%`, width: widthPx }}
    >
      {layer === 'column' && (
        <VestingGraphIntroReveal
          className={twMerge('absolute inset-0 border-x border-dashed', columnClassName)}
          delayMs={introDelayForReveal(leftPercent)}
          phase={introPhase}
          variant="grow"
        />
      )}
      {layer === 'badge' && (
        <div className="absolute inset-x-0 top-0 z-[1] h-0">
          <div
            className="absolute left-1/2 top-0"
            style={{ transform: BADGE_STACK_TRANSFORM[stack] }}
          >
            <VestingGraphIntroReveal delayMs={introDelayForReveal(leftPercent)} phase={introPhase}>
              <VestingGraphMilestoneBadge
                className={twMerge('rounded px-2 py-0.5', badgeClassName)}
                icon={icon}
                label={label}
              />
            </VestingGraphIntroReveal>
          </div>
        </div>
      )}
    </div>
  );
};

type VestingGraphDatedSkinnyColumnMarkerProps = {
  bottomInsetPx: number;
  label: ReactNode;
  layer: VestingGraphOverlayLayer;
  leftPercent: number;
  stack?: VestingGraphEventMarkerStack;
  widthPx: number;
};

export const VestingGraphAccelerationMarker: FC<VestingGraphDatedSkinnyColumnMarkerProps> = (
  props,
) => (
  <VestingGraphSkinnyColumnMarker
    {...props}
    badgeClassName="bg-forest-500"
    columnClassName="border-forest-200 bg-gradient-to-b from-forest-50 to-white"
    icon={<BoltIcon height={11} iconColor="#FFFFFF" width={11} />}
  />
);

export const VestingGraphExpiryMarker: FC<VestingGraphDatedSkinnyColumnMarkerProps> = (props) => (
  <VestingGraphSkinnyColumnMarker
    {...props}
    badgeClassName="bg-fireside-500"
    columnClassName="border-fireside-400 bg-gradient-to-b from-fireside-50 to-white"
    icon={<CloseCircleIcon height={12} iconColor="#FFFFFF" width={12} />}
  />
);

type VestingGraphTargetMilestoneColumnProps = {
  leftPercent: number;
  widthPercent: number;
  /** Wash rises only to this plot % — earlier targets stay lower, matching the area steps. */
  yPercent: number;
};

type VestingGraphTargetMilestoneColumnEdgesProps = {
  leftPercent: number;
  widthPercent: number;
  /** Rails stop at this plot % — same height as the wash / area step, not the full Y axis. */
  yPercent: number;
};

/**
 * Dashed rails for each M-window, from the plot baseline up to that target's
 * cumulative %. Sit on the badge overlay so the area series never paints over them.
 */
export const VestingGraphTargetMilestoneColumnEdges: FC<
  VestingGraphTargetMilestoneColumnEdgesProps
> = ({ leftPercent, widthPercent, yPercent }) => {
  const {
    view: { introDelayForReveal, introPhase },
  } = useVestingGraphContext();

  return (
    <VestingGraphIntroReveal
      className="pointer-events-none absolute bottom-0 border-x border-dashed border-brand-100"
      delayMs={introDelayForReveal(leftPercent)}
      phase={introPhase}
      style={{
        height: `${Math.min(Math.max(yPercent, 0), 100)}%`,
        left: `${leftPercent}%`,
        width: `${Math.max(widthPercent, 0)}%`,
      }}
      variant="grow"
    />
  );
};

/**
 * Brand wash only — edges render separately above the series. Fill stops at this
 * target's cumulative % so M1…Mn columns step up with the badges.
 */
export const VestingGraphTargetMilestoneColumn: FC<VestingGraphTargetMilestoneColumnProps> = ({
  leftPercent,
  widthPercent,
  yPercent,
}) => {
  const {
    view: { introDelayForReveal, introPhase },
  } = useVestingGraphContext();

  return (
    <VestingGraphIntroReveal
      className="pointer-events-none absolute bottom-0 bg-gradient-to-b from-brand-25 to-white"
      delayMs={introDelayForReveal(leftPercent)}
      phase={introPhase}
      style={{
        height: `${Math.min(Math.max(yPercent, 0), 100)}%`,
        left: `${leftPercent}%`,
        width: `${Math.max(widthPercent, 0)}%`,
      }}
      variant="grow"
    />
  );
};

type VestingGraphTargetMilestoneBracketProps = {
  description: string;
  label: string;
  leftPercent: number;
  widthPercent: number;
  /** Plot height % (0–100) for the badge rule — steps up with each target's cumulative %. */
  yPercent: number;
};

/**
 * M-label on the top edge of that target's area segment, with the description above the
 * rule. Later milestones sit higher, matching the rising fill. Column wash stays under the
 * series.
 */
export const VestingGraphTargetMilestoneBracket: FC<VestingGraphTargetMilestoneBracketProps> = ({
  description,
  label,
  leftPercent,
  widthPercent,
  yPercent,
}) => {
  const {
    view: { introDelayForReveal, introPhase },
  } = useVestingGraphContext();

  return (
    <div
      className="pointer-events-none absolute"
      style={{
        left: `${leftPercent}%`,
        top: `${vestingGraphPercentToOverlayTopPercent(yPercent)}%`,
        width: `${Math.max(widthPercent, 0)}%`,
      }}
    >
      <div className="absolute inset-x-0 top-0 -translate-y-1/2">
        <VestingGraphIntroReveal
          delayMs={introDelayForReveal(leftPercent + widthPercent / 2)}
          phase={introPhase}
        >
          {description && (
            <p className="absolute inset-x-0 bottom-full mb-1 line-clamp-2 px-1 text-center text-[9px] font-medium leading-3 text-gray-700">
              {description}
            </p>
          )}
          <VestingGraphBracketRule badgeClassName="rounded" label={label} tone="brand" />
        </VestingGraphIntroReveal>
      </div>
    </div>
  );
};

type VestingGraphMilestoneColumnCursorProps = {
  className?: string;
  height?: number;
  points?: Array<{ x: number; y: number }>;
  top?: number;
};

/**
 * Same dashed vertical cursor as the bar charts, but tracked to the pointer so a wide
 * target column is one hover — not a jump between the area ramp's start and end.
 */
export const VestingGraphMilestoneColumnCursor: FC<VestingGraphMilestoneColumnCursorProps> = ({
  className,
  height = 0,
  points,
  top = 0,
}) => {
  const {
    view: { chartPointer },
  } = useVestingGraphContext();
  const start = points?.[0];
  const end = points?.[1];
  const x = chartPointer?.x ?? start?.x;
  if (x == null) return null;

  return (
    <line
      className={className}
      pointerEvents="none"
      stroke={VESTING_GRAPH_TOOLTIP_CURSOR_COLOR}
      strokeDasharray="4 4"
      strokeWidth={1}
      x1={x}
      x2={x}
      y1={start?.y ?? top}
      y2={end?.y ?? top + height}
    />
  );
};
